import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Phase 3.1 Tier 2 — Integrations marketplace grid.
//
// Handles the client-side filter chips + fuzzy search. The card list
// is small (today ~10, growing toward ~50) so a backend round-trip
// would only add latency without saving any meaningful work — every
// card ships its searchable strings on data-* attrs and we filter in
// place.
//
// Mixpanel:
//   * Marketplace Viewed — fires server-side from the controller.
//   * Marketplace Filter Applied — client-side; debounced to avoid
//     one event per keystroke.
export default class extends Controller {
  static targets = ["chip", "card", "search", "noMatches", "cardWrap", "grid", "section"]

  connect() {
    this._activeCategory = "all"
    this._searchTerm = ""
    this._debounceTimer = null
  }

  filterByCategory(event) {
    const cat = event.currentTarget.dataset.category || "all"
    this._activeCategory = cat
    this._refreshChipStyles()
    this._applyFilters()
    track("Integration Marketplace Filter Applied", {
      category: cat,
      search_query_present: this._searchTerm.length > 0
    })
  }

  search(event) {
    this._searchTerm = (event.target.value || "").toLowerCase().trim()
    this._applyFilters()
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      track("Integration Marketplace Filter Applied", {
        category: this._activeCategory,
        search_query_length: this._searchTerm.length
      })
    }, 600)
  }

  clearFilters() {
    this._activeCategory = "all"
    this._searchTerm = ""
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this._refreshChipStyles()
    this._applyFilters()
  }

  // ── Internals ──────────────────────────────────────────────────

  _refreshChipStyles() {
    if (!this.hasChipTarget) return
    this.chipTargets.forEach(chip => {
      const isActive = chip.dataset.category === this._activeCategory
      chip.setAttribute("aria-selected", isActive ? "true" : "false")
      if (isActive) {
        chip.classList.remove("bg-white", "text-gray-600", "hover:bg-gray-50")
        chip.classList.add("bg-brand", "text-white")
      } else {
        chip.classList.add("bg-white", "text-gray-600", "hover:bg-gray-50")
        chip.classList.remove("bg-brand", "text-white")
      }
    })
  }

  _applyFilters() {
    if (!this.hasCardTarget) return

    let visibleCount = 0
    const term = this._searchTerm

    this.cardTargets.forEach(card => {
      const cat = card.dataset.categorySlug || ""
      const haystack = [
        card.dataset.providerName || "",
        card.dataset.providerTagline || "",
        card.dataset.providerDescription || ""
      ].join(" ")

      const categoryMatch =
        this._activeCategory === "all" || this._activeCategory === cat
      const searchMatch = term.length === 0 || haystack.indexOf(term) !== -1

      const show = categoryMatch && searchMatch
      card.classList.toggle("hidden", !show)
      if (show) visibleCount += 1
    })

    // Hide a category section (header + grid) when none of its cards
    // survive the current filter, so we never leave a stray title.
    if (this.hasSectionTarget) {
      this.sectionTargets.forEach(section => {
        const anyVisible = Array.from(
          section.querySelectorAll('[data-marketplace-grid-target="card"]')
        ).some(card => !card.classList.contains("hidden"))
        section.classList.toggle("hidden", !anyVisible)
      })
    }

    if (this.hasNoMatchesTarget) {
      const empty = visibleCount === 0
      this.noMatchesTarget.classList.toggle("hidden", !empty)
      if (this.hasCardWrapTarget) {
        this.cardWrapTarget.classList.toggle("hidden", empty)
      }
    }
  }
}
